import React, { MouseEvent, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { BreadcrumbItem, mapBreadcrumb } from '../../utils/breadcrumb-item';
import './app-breadcrumb.scss';

export interface AppBreadcrumbProps {
  customItems?: BreadcrumbItem[];
}

export const AppBreadcrumb: React.FC<AppBreadcrumbProps> = ({ customItems }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { formatMessage } = useIntl();
  const items: BreadcrumbItem[] = useMemo(
    () => customItems || location?.pathname.split('/').flatMap((item) => mapBreadcrumb[item] || []),
    [customItems, location]
  );

  const linkTo = useCallback(
    (event: MouseEvent<HTMLSpanElement>) => {
      const id = (event.target as HTMLSpanElement).id;
      const targetItem = items.find((it) => it.key === id) || mapBreadcrumb[id];
      if (targetItem?.path) {
        navigate(targetItem.path);
      }
    },
    [items, navigate]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLSpanElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const id = (event.target as HTMLSpanElement).id;
        const targetItem = items.find((it) => it.key === id) || mapBreadcrumb[id];
        if (targetItem?.path) {
          navigate(targetItem.path);
        }
      }
    },
    [items, navigate]
  );

  const title = document.getElementsByTagName('title')[0];
  if (title && items.length > 0 && items[items.length - 1]?.label) {
    title.textContent = 'MyAzimut - ' + formatMessage(items[items.length - 1].label);
  }

  return (
    <div className='app-breadcrumb'>
      {items?.map((item, index) => (
        <span
          className='app-breadcrumb-item'
          key={item.key}
          aria-selected={index === items.length - 1}
          onClick={linkTo}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          id={item.key}>
          {formatMessage(item.label)}
          {index !== items.length - 1 ? ' / ' : ''}
        </span>
      ))}
    </div>
  );
};
